import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  FlaskConical,
  Atom,
  Leaf,
  Sigma,
  GraduationCap,
  Clock,
  Sparkles,
  Star,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ResourcesSection } from "@/components/ResourcesSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LANDING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const subjects = [
  {
    id: "chemistry",
    name: "Chemistry",
    icon: FlaskConical,
    blurb: "From bonding & moles to A-Level mechanisms — broken down step by step.",
    levels: ["KS3", "KS4", "KS5 · A-Level"],
    testId: LANDING.subjectChemistry,
  },
  {
    id: "physics",
    name: "Physics",
    icon: Atom,
    blurb: "Forces, energy, waves and electricity made intuitive with worked examples.",
    levels: ["KS3", "KS4"],
    testId: LANDING.subjectPhysics,
  },
  {
    id: "biology",
    name: "Biology",
    icon: Leaf,
    blurb: "Cells, ecosystems, genetics, exam-ready answers and clear diagrams.",
    levels: ["KS3", "KS4"],
    testId: LANDING.subjectBiology,
  },
  {
    id: "maths",
    name: "Maths",
    icon: Sigma,
    blurb: "Algebra to trigonometry. Build confidence with practice and patient guidance.",
    levels: ["KS3", "KS4"],
    testId: LANDING.subjectMaths,
  },
];

// Hardcoded testimonials list is no longer used — they are fetched from the API.

export default function Landing() {
  const navigate = useNavigate();
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((res) => setTestimonials(res.data?.testimonials || []))
      .catch(() => {});
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success("Message sent, I'll get back to you within 24 hours.");
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again or email me directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBundleInterest = (label) => {
    setContact((c) => ({
      ...c,
      message:
        c.message ||
        `Hi Kenisha, I'd love to book the ${label}. Please could you send me an invoice and we can schedule the sessions? Thanks!`,
    }));
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#fbfcfe] text-[#2a3b5c]">
      <Navbar />

      {/* HERO */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden paper-grain">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] terracotta-blob pointer-events-none" />
        <div className="absolute top-40 -left-32 w-[320px] h-[320px] sage-blob pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#f0f6fc] border border-[#dce6f0] text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                <Sparkles className="w-3.5 h-3.5" /> 1-to-1 Tuition · KS3 · KS4 · A-Level
              </span>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-[#2a3b5c]">
                Science &amp; Maths,
                <br />
                taught the way
                <br />
                <span className="text-[#3d6fa8]">you’ll actually get it.</span>
              </h1>
              <p className="mt-7 text-lg text-[#5c6b85] leading-relaxed max-w-xl">
                I’m a full-time science teacher with three years in the
                classroom, offering friendly 1-to-1 online tuition in
                Chemistry, Physics, Biology and Maths, and A-Level Chemistry
                for Y12 &amp; Y13.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Button
                  data-testid={LANDING.heroBookButton}
                  onClick={() => navigate("/book")}
                  className="rounded-full px-7 py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] active:scale-95 transition-all duration-300"
                >
                  Book a session · from £30 / hr
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button
                  data-testid={LANDING.heroLearnMoreButton}
                  variant="ghost"
                  onClick={() => {
                    document.getElementById("subjects")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="rounded-full px-6 py-6 text-base text-[#2a3b5c] hover:bg-[#f0f6fc]"
                >
                  See subjects &amp; levels
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#5c6b85]">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#ffe89a]" /> Active science teacher
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#ffe89a]" /> 3+ years experience
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#ffe89a]" /> Online sessions, UK
                </span>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5 relative max-w-[420px] mx-auto lg:max-w-none"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="absolute -inset-5 bg-[#8ab8e8]/15 rounded-[2rem] -rotate-3" />
              <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-3xl bg-[#ffe89a]/40 -z-0" />
              <img
                src="https://customer-assets.emergentagent.com/job_sciencetutor-hub-1/artifacts/98us0ngy_IMG_9511.jpg"
                alt="Kenisha — Science & Maths tutor"
                className="relative w-full aspect-square object-cover object-center rounded-3xl shadow-sm"
              />
              <div className="absolute -bottom-5 right-5 bg-[#fbfcfe] border border-[#dce6f0] rounded-2xl px-4 py-3 shadow-sm flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#ffe89a]/30 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-[#2a3b5c]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#5c6b85]">Session</div>
                  <div className="font-heading font-semibold text-[#2a3b5c] text-sm">60 min · from £30</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SUBJECTS */}
      <section id="subjects" className="py-24 md:py-32 bg-[#fbfcfe]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              Subjects &amp; levels
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
              Built around the syllabus, shaped around your child.
            </h2>
            <p className="mt-5 text-base text-[#5c6b85] leading-relaxed">
              Each session is planned from your topic list and tailored to the
              exam board. We work through past papers, exam technique, and the
              tricky bits that don’t click in a classroom of thirty.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {subjects.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.id}
                  data-testid={s.testId}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group bg-[#f0f6fc] border border-[#dce6f0] rounded-3xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#fbfcfe] border border-[#dce6f0] flex items-center justify-center text-[#3d6fa8] group-hover:bg-[#8ab8e8] group-hover:text-[#2a3b5c] transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-semibold text-[#2a3b5c]">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm text-[#5c6b85] leading-relaxed min-h-[72px]">
                    {s.blurb}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.levels.map((lv) => (
                      <span
                        key={lv}
                        className="text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full bg-[#ffe89a]/30 text-[#2a3b5c]"
                      >
                        {lv}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-24 md:py-32 bg-[#f0f6fc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <motion.div {...fadeUp} className="lg:col-span-5 relative max-w-[420px] mx-auto lg:max-w-none">
              <div className="absolute -inset-4 bg-[#ffe89a]/40 rounded-[2rem] rotate-2" />
              <img
                src="https://customer-assets.emergentagent.com/job_sciencetutor-hub-1/artifacts/98us0ngy_IMG_9511.jpg"
                alt="Portrait of Kenisha"
                className="relative w-full aspect-square object-cover object-center rounded-3xl"
              />
            </motion.div>

            <motion.div {...fadeUp} className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                About me
              </div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
                Hello — I’m Kenisha.
              </h2>
              <p className="mt-6 text-lg text-[#5c6b85] leading-relaxed">
                I’m a full-time science teacher with three years’ experience
                in UK secondary schools, currently in the classroom every
                day. I hold a Master’s in Psychology and an undergraduate
                degree in Biomedical Science, together they shape how I
                teach: with patience for how young people actually learn,
                and the depth needed to make science genuinely make sense.
              </p>
              <p className="mt-4 text-base text-[#5c6b85] leading-relaxed">
                I tutor across all three major exam boards, <strong className="text-[#2a3b5c]">AQA, OCR and Edexcel</strong>,
                so sessions slot directly into what your child is covering
                at school. We’ll close gaps, build confidence and rehearse
                exam technique until it feels routine.
              </p>
              <p className="mt-4 text-base text-[#5c6b85] leading-relaxed">
                I tutor because I genuinely believe 1-to-1 teaching can
                transform the quality of a young person’s education. There
                is only so much one teacher can offer a class of thirty,
                in a focused session, I can listen properly, spot what’s
                holding them back, and commit to being a real part of their
                development.
              </p>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-5">
                <div className="rounded-3xl bg-[#fbfcfe] border border-[#dce6f0] p-5">
                  <GraduationCap className="w-6 h-6 text-[#3d6fa8]" />
                  <div className="mt-3 font-heading text-2xl font-semibold text-[#2a3b5c]">Master’s</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#5c6b85] mt-1">
                    Psychology
                  </div>
                </div>
                <div className="rounded-3xl bg-[#fbfcfe] border border-[#dce6f0] p-5">
                  <FlaskConical className="w-6 h-6 text-[#3d6fa8]" />
                  <div className="mt-3 font-heading text-2xl font-semibold text-[#2a3b5c]">BSc</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#5c6b85] mt-1">
                    Biomedical Science
                  </div>
                </div>
                <div className="rounded-3xl bg-[#fbfcfe] border border-[#dce6f0] p-5 col-span-2 md:col-span-1">
                  <Sparkles className="w-6 h-6 text-[#3d6fa8]" />
                  <div className="mt-3 font-heading text-2xl font-semibold text-[#2a3b5c]">AQA · OCR · Edexcel</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#5c6b85] mt-1">
                    Exam boards
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 bg-[#fbfcfe]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              Pricing
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
              Simple pricing. No surprises.
            </h2>
            <p className="mt-5 text-base text-[#5c6b85]">
              Every session is one hour, online, 1-to-1. Cancel or
              reschedule up to 24 hours before, no charge.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="mt-14 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* KS3 / KS4 / GCSE */}
            <div className="rounded-3xl border-2 border-[#dce6f0] bg-[#fbfcfe] p-9 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-[#ffe89a]/40 rounded-full" />
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                KS3 · KS4 / GCSE
              </div>
              <div className="mt-3 text-sm text-[#5c6b85]">
                Year 7 – Year 11 · Chemistry, Physics, Biology, Maths
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-heading text-5xl lg:text-6xl font-semibold text-[#2a3b5c]">
                  £30
                </span>
                <span className="text-[#5c6b85]">/ hour</span>
              </div>
              <ul className="mt-7 space-y-3 text-sm text-[#5c6b85]">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  60-minute 1-to-1 session
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  Planned around your topic list &amp; exam board
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  Follow-up notes &amp; practice questions
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  Free reschedule up to 24h before
                </li>
              </ul>
              <Button
                data-testid={LANDING.pricingBookButton}
                onClick={() => navigate("/book")}
                className="mt-9 w-full rounded-full py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] active:scale-95 transition-all duration-300"
              >
                Book KS3 / KS4
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* KS5 / A-Level */}
            <div className="rounded-3xl border-2 border-[#8ab8e8] bg-[#fbfcfe] p-9 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-44 h-44 terracotta-blob" />
              <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 rounded-full bg-[#ffe89a]/70 text-[#2a3b5c]">
                A-Level
              </span>
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                KS5 / A-Level Chemistry
              </div>
              <div className="mt-3 text-sm text-[#5c6b85]">
                Year 12 &amp; Year 13 · A-Level Chemistry
              </div>
              <div className="mt-5 flex items-baseline gap-2">
                <span className="font-heading text-5xl lg:text-6xl font-semibold text-[#2a3b5c]">
                  £40
                </span>
                <span className="text-[#5c6b85]">/ hour</span>
              </div>
              <ul className="mt-7 space-y-3 text-sm text-[#5c6b85]">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  60-minute 1-to-1 session
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  AQA, OCR &amp; Edexcel A-Level specs
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  Exam-style questions with full markscheme breakdown
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#ffe89a] mt-0.5 shrink-0" />
                  Free reschedule up to 24h before
                </li>
              </ul>
              <Button
                data-testid={`${LANDING.pricingBookButton}-ks5`}
                onClick={() => navigate("/book")}
                className="mt-9 w-full rounded-full py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] active:scale-95 transition-all duration-300"
              >
                Book A-Level
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </motion.div>

          {/* BUNDLES */}
          <motion.div {...fadeUp} className="mt-16 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-4 h-4 text-[#3d6fa8]" />
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                Save with a bundle
              </div>
            </div>

            <div className="rounded-3xl border border-[#dce6f0] bg-[#fbfcfe] overflow-hidden">
              <div className="px-7 py-6 border-b border-[#dce6f0] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                <h3 className="font-heading text-2xl font-semibold text-[#2a3b5c]">
                  Block-book and save up to £50
                </h3>
                <p className="text-sm text-[#5c6b85]">
                  Paid upfront via Stripe invoice. Schedule the sessions together with me.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-[#dce6f0]">
                {[
                  {
                    label: "KS3 / KS4 · 5 sessions",
                    price: 140,
                    save: 10,
                    perSession: 28,
                    testId: "bundle-ks34-5",
                  },
                  {
                    label: "KS3 / KS4 · 10 sessions",
                    price: 270,
                    save: 30,
                    perSession: 27,
                    testId: "bundle-ks34-10",
                  },
                  {
                    label: "A-Level · 5 sessions",
                    price: 185,
                    save: 15,
                    perSession: 37,
                    testId: "bundle-ks5-5",
                  },
                  {
                    label: "A-Level · 10 sessions",
                    price: 360,
                    save: 40,
                    perSession: 36,
                    testId: "bundle-ks5-10",
                  },
                ].map((b) => (
                  <div
                    key={b.testId}
                    data-testid={b.testId}
                    className="p-6 flex flex-col"
                  >
                    <div className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#3d6fa8]">
                      {b.label}
                    </div>
                    <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="font-heading text-3xl font-semibold text-[#2a3b5c]">
                        £{b.price}
                      </span>
                      <span className="text-xs text-[#5c6b85]">total</span>
                    </div>
                    <div className="mt-1 text-xs text-[#5c6b85]">
                      £{b.perSession} / session
                    </div>
                    <span className="mt-3 self-start inline-block text-[10px] uppercase tracking-[0.15em] font-medium px-2 py-1 rounded-full bg-[#ffe89a]/60 text-[#2a3b5c]">
                      Save £{b.save}
                    </span>
                    <button
                      data-testid={`${b.testId}-cta`}
                      onClick={() => handleBundleInterest(b.label)}
                      className="mt-auto pt-5 text-sm font-medium text-[#3d6fa8] hover:text-[#2a3b5c] inline-flex items-center gap-1.5 transition-colors text-left"
                    >
                      Enquire
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-4 text-xs text-[#5c6b85] text-center">
              Bundle sessions must be used within 6 months. Free reschedule up to 24h before each session.
            </p>
          </motion.div>
        </div>
      </section>

      {/* RESOURCES */}
      <ResourcesSection />

      {/* TESTIMONIALS — renders only when reviews are added */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-24 md:py-32 bg-[#f0f6fc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp} className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              What families say
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
              Calm, clear and exam-ready.
            </h2>
          </motion.div>

          <div className="mt-12 flex gap-6 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4 -mx-6 px-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="snap-start shrink-0 w-[320px] md:w-[400px] bg-[#fbfcfe] border border-[#dce6f0] rounded-3xl p-8"
              >
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-[#3d6fa8] text-[#3d6fa8]" />
                  ))}
                </div>
                <p className="mt-5 text-[#2a3b5c] leading-relaxed font-heading text-lg">
                  “{t.quote}”
                </p>
                <div className="mt-6 text-sm text-[#5c6b85]">— {t.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 bg-[#fbfcfe]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div {...fadeUp}>
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              Questions?
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]">
              Send me a quick message.
            </h2>
            <p className="mt-5 text-base text-[#5c6b85]">
              Not ready to book? Tell me about the student, the subject and
              where they’re at, I’ll reply within 24 hours. You can also email
              me directly at{" "}
              <a
                href="mailto:Kenishabossa@gmail.com"
                className="text-[#3d6fa8] font-medium hover:underline"
              >
                Kenishabossa@gmail.com
              </a>
              .
            </p>

            <form
              onSubmit={handleContact}
              className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Input
                data-testid={LANDING.contactNameInput}
                placeholder="Your name"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
              />
              <Input
                data-testid={LANDING.contactEmailInput}
                placeholder="Email address"
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                className="rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
              />
              <Textarea
                data-testid={LANDING.contactMessageInput}
                placeholder="What would you like to ask?"
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="md:col-span-2 rounded-2xl min-h-[140px] bg-[#f0f6fc] border-[#dce6f0]"
              />
              <Button
                data-testid={LANDING.contactSubmitButton}
                type="submit"
                disabled={submitting}
                className="md:col-span-2 rounded-full py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] active:scale-95 transition-all"
              >
                {submitting ? "Sending..." : "Send message"}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
